import type { StorageManager } from '@/storage/save-data';
import type { NameSuggestions, SigninResult, SignupResult } from './sync-types';
import * as api from './api-client';

export class Account {
  constructor(private sm: StorageManager) {}

  isSignedIn(): boolean {
    return this.sm.getSession() !== undefined;
  }

  getSession() {
    return this.sm.getSession();
  }

  getSuggestions(count: number): Promise<NameSuggestions> {
    return api.getSuggestions(count);
  }

  async signup(displayName: string, nonce: string): Promise<SignupResult> {
    const r = await api.signup({ displayName, nonce });
    this.sm.setSession({
      token: r.sessionToken,
      userId: r.userId,
      displayName: r.displayName,
      discriminator: r.discriminator,
    });
    return {
      userId: r.userId,
      displayName: r.displayName,
      discriminator: r.discriminator,
      pin: r.pin,
      recoveryCode: r.recoveryCode,
    };
  }

  async signin(displayName: string, pin: string, discriminator?: string): Promise<SigninResult> {
    const r = await api.login({ displayName, pin, ...(discriminator ? { discriminator } : {}) });
    this.sm.setSession({
      token: r.sessionToken,
      userId: r.userId,
      displayName: r.displayName,
      discriminator: r.discriminator,
    });
    return { userId: r.userId, displayName: r.displayName, discriminator: r.discriminator };
  }

  async recover(displayName: string, recoveryCode: string, discriminator?: string): Promise<SignupResult> {
    const r = await api.recover({ displayName, recoveryCode, ...(discriminator ? { discriminator } : {}) });
    this.sm.setSession({
      token: r.sessionToken,
      userId: r.userId,
      displayName: r.displayName,
      discriminator: r.discriminator,
    });
    return {
      userId: r.userId,
      displayName: r.displayName,
      discriminator: r.discriminator,
      pin: r.pin,
      recoveryCode: r.recoveryCode,
    };
  }

  async rotatePin(): Promise<{ pin: string }> {
    const sess = this.requireSession();
    return api.rotatePin(sess.token);
  }

  async rotateRecovery(pin: string): Promise<{ recoveryCode: string }> {
    const sess = this.requireSession();
    return api.rotateRecovery(sess.token, { pin });
  }

  async logout(): Promise<void> {
    const sess = this.sm.getSession();
    if (sess) {
      try { await api.logout(sess.token); } catch { /* tolerate server failure */ }
    }
    this.sm.clearSession();
  }

  private requireSession() {
    const s = this.sm.getSession();
    if (!s) throw new Error('Not signed in');
    return s;
  }
}
